import { useState, useMemo } from "react";
import { Plus, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { useStore } from "../store";
import { AppointmentUndo } from "../components/AppointmentUndo";
import {
  appointmentDates,
  dateKey,
  addDays,
  dateLabel,
  formatTime,
  durationLabel,
  type Appointment,
} from "../../shared/domain";
import { WhatsAppAction } from "../components/WhatsAppAction";
import { whatsappUrl } from "../../shared/phone";
import { AppointmentDetails } from "../components/AppointmentDetails";
import { AppointmentIndicator } from "../components/AppointmentIndicator";
import { CalendarPicker } from "../components/CalendarPicker";
import { AppointmentForm } from "../components/AppointmentForm";
import { Empty } from "../components/ui";
export function Schedule() {
  const { data, offline } = useStore();
  const s = data!.settings,
    today = dateKey(Date.now(), s.timezone);
  const appointmentDays = useMemo(
    () => appointmentDates(data!.appointments, s.timezone),
    [data, s.timezone],
  );
  const [details, setDetails] = useState<Appointment | null>(null);
  const [picker, setPicker] = useState(false);
  const [day, setDay] = useState(today),
    [view, setView] = useState<"day" | "upcoming">("day"),
    [editor, setEditor] = useState<Appointment | "new" | null>(null),
    [undo, setUndo] = useState<{ a: Appointment; until: number } | null>(null);
  const selected = data!.appointments.filter(
    (a) =>
      dateKey(a.start, s.timezone) <= day &&
      dateKey(a.start + a.duration * 60000 - 1, s.timezone) >= day,
  );
  const future = data!.appointments.filter(
    (a) => a.start + a.duration * 60000 > Date.now(),
  );
  const groups =
    view === "day"
      ? [[day, selected] as const]
      : Array.from(
          new Set(future.map((a) => dateKey(a.start, s.timezone))),
        ).map(
          (d) =>
            [
              d,
              future.filter((a) => dateKey(a.start, s.timezone) === d),
            ] as const,
        );
  const weekday = (new Date(day + "T12:00Z").getUTCDay() + 6) % 7,
    monday = addDays(day, -weekday);
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">YOUR TIME, BEAUTIFULLY PLANNED</p>
          <h1>
            Schedule<span className="accent">.</span>
          </h1>
        </div>
        <button
          className="primary desktop-add"
          disabled={offline}
          onClick={() => setEditor("new")}
        >
          <Plus size={18} />
          New appointment
        </button>
      </div>
      <div className="schedule-layout">
        <section
          className={
            "calendar-panel " + (view === "upcoming" ? "show-upcoming" : "")
          }
        >
          <div className="segmented" aria-label="Schedule view">
            {(["day", "upcoming"] as const).map((v) => (
              <button
                key={v}
                className={view === v ? "active" : ""}
                onClick={() => setView(v)}
              >
                {v === "day" ? "Day" : "Upcoming"}
              </button>
            ))}
          </div>
          <div className="date-heading">
            <button
              className="eyebrow date-picker-trigger"
              aria-label="Choose month and year"
              aria-haspopup="dialog"
              aria-expanded={picker}
              onClick={() => setPicker(true)}
            >
              {dateLabel(day, { month: "long", year: "numeric" })}
            </button>
            <h2>
              <button
                className="date-picker-trigger"
                aria-label="Choose schedule date"
                aria-haspopup="dialog"
                aria-expanded={picker}
                onClick={() => setPicker(true)}
              >
                {dateLabel(day, { weekday: "long", day: "numeric" })}
              </button>
            </h2>
          </div>
          <div className="day-controls">
            <button
              className="icon-button"
              aria-label="Previous day"
              onClick={() => {
                setDay((current) => addDays(current, -1));
                setView("day");
              }}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="today-button"
              onClick={() => {
                setDay(today);
                setView("day");
              }}
            >
              Today
            </button>
            <button
              className="icon-button"
              aria-label="Next day"
              onClick={() => {
                setDay((current) => addDays(current, 1));
                setView("day");
              }}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <div className="week">
            {Array.from({ length: 7 }, (_, i) => {
              const d = addDays(monday, i);
              return (
                <button
                  key={d}
                  className={d === day ? "selected" : ""}
                  onClick={() => {
                    setDay(d);
                    setView("day");
                  }}
                >
                  <span>{dateLabel(d, { weekday: "short" })}</span>
                  <strong>{Number(d.slice(-2))}</strong>
                  <AppointmentIndicator
                    hasAppointments={appointmentDays.has(d)}
                  />
                </button>
              );
            })}
          </div>
          <div className="calendar-footer">
            <span className="small-dot" />
            All times in {s.timezone}
          </div>
        </section>
        <section className="timeline-panel">
          <div className="section-heading">
            <h2>
              {view === "day"
                ? day === today
                  ? "Your day"
                  : "Appointments"
                : "On the horizon"}
            </h2>
            <span>
              {(view === "day" ? selected : future).length} appointment
              {(view === "day" ? selected : future).length === 1 ? "" : "s"}
            </span>
          </div>
          {groups.length === 0 || (view === "day" && !selected.length) ? (
            <Empty
              title={
                view === "day"
                  ? "A little breathing room."
                  : "A fresh page awaits."
              }
              description={
                view === "day"
                  ? "Nothing scheduled for this day. Make room for your next creation."
                  : "Your upcoming appointments will appear here."
              }
              action={
                <button
                  className="primary"
                  disabled={offline}
                  onClick={() => setEditor("new")}
                >
                  Add appointment
                  <ArrowUpRight size={17} />
                </button>
              }
            />
          ) : (
            groups.map(([date, appointments]) => (
              <div key={date}>
                {view === "upcoming" && (
                  <h3 className="group-date">
                    {date === today
                      ? "Today · "
                      : date === addDays(today, 1)
                        ? "Tomorrow · "
                        : ""}
                    {dateLabel(date)}
                  </h3>
                )}
                <div className="timeline">
                  {appointments.map((a, i) => {
                    const gap = i
                      ? Math.round(
                          (a.start -
                            (appointments[i - 1].start +
                              appointments[i - 1].duration * 60000)) /
                            60000,
                        )
                      : 0;
                    return (
                      <div key={a.id}>
                        {view === "day" && gap >= 30 && (
                          <div className="gap">
                            {durationLabel(gap)} between appointments
                          </div>
                        )}
                        <article className="appointment-row">
                          <div className="time">
                            <span className="timeline-dot" />
                            {formatTime(a.start, s)}
                            {dateKey(a.start, s.timezone) !== date && (
                              <small>Previous day</small>
                            )}
                          </div>
                          <div className="appointment-card">
                            <button
                              className="appointment-open"
                              aria-label={`View appointment for ${a.name}`}
                              onClick={() => setDetails(a)}
                            >
                              <span className="client-title">{a.name}</span>
                              <span className="muted">
                                {durationLabel(a.duration)}
                                <span className="separator">·</span>Ends{" "}
                                {formatTime(a.start + a.duration * 60000, s)}
                              </span>
                            </button>
                            {whatsappUrl(a.phone) ? (
                              <WhatsAppAction
                                phone={a.phone}
                                name={a.name}
                                compact
                              />
                            ) : (
                              <ChevronRight size={16} className="muted" />
                            )}
                          </div>
                        </article>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </section>
      </div>
      <button
        className="fab"
        aria-label="New appointment"
        disabled={offline}
        onClick={() => setEditor("new")}
      >
        <Plus size={27} />
      </button>
      {picker && (
        <CalendarPicker
          value={day}
          today={today}
          appointmentDays={appointmentDays}
          onSelect={(date) => {
            setDay(date);
            setView("day");
          }}
          onClose={() => setPicker(false)}
        />
      )}
      {details && (
        <AppointmentDetails
          appointment={
            data!.appointments.find((a) => a.id === details.id) ?? details
          }
          onClose={() => setDetails(null)}
          onDelete={(a, until) => setUndo({ a, until })}
        />
      )}
      {editor && (
        <AppointmentForm
          appointment={editor === "new" ? undefined : editor}
          date={day}
          onClose={() => setEditor(null)}
          onDelete={(a, until) => setUndo({ a, until })}
        />
      )}
      {undo && <AppointmentUndo undo={undo} onClose={() => setUndo(null)} />}
    </>
  );
}
